import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { customServiceRequestApi } from '../../api/customServiceRequest';

// 获取定制服务请求列表
export const fetchCustomServiceRequestList = createAsyncThunk(
  'customServiceRequest/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      // 参数名适配：current/pageSize -> pageNum/pageSize，其余透传
      const { current, pageSize, ...rest } = params || {};
      const apiParams = {
        pageNum: current || 1,
        pageSize: pageSize || 10,
        ...rest
      };
      const response = await customServiceRequestApi.getCustomServiceRequestPage(apiParams);
      // 后端返回的是 IPage 格式，包含 records, total, current, size 等字段
      const data = response.data || response;
      return {
        list: data.records || data.list || [],
        total: data.total || 0,
        current: data.current || params.current || 1,
        pageSize: data.size || params.pageSize || 10
      };
    } catch (error) {
      console.error('获取定制服务请求列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取定制服务请求列表失败' });
    }
  }
);

// 获取定制服务请求详情
export const fetchCustomServiceRequestDetail = createAsyncThunk(
  'customServiceRequest/fetchDetail',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await customServiceRequestApi.getCustomServiceRequestDetail(requestId);
      return response.data || response;
    } catch (error) {
      console.error('获取定制服务请求详情失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取定制服务请求详情失败' });
    }
  }
);

// 更新定制服务需求
export const updateCustomServiceRequest = createAsyncThunk(
  'customServiceRequest/update',
  async (data, { rejectWithValue }) => {
    try {
      const response = await customServiceRequestApi.updateCustomServiceRequest(data);
      message.success('更新成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '更新失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 删除定制服务需求
export const deleteCustomServiceRequest = createAsyncThunk(
  'customServiceRequest/delete',
  async (requestId, { rejectWithValue, dispatch }) => {
    try {
      await customServiceRequestApi.deleteCustomServiceRequest(requestId);
      message.success('删除成功');
      // 刷新列表
      dispatch(fetchCustomServiceRequestList());
      return requestId;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 审核定制服务需求
export const reviewCustomServiceRequest = createAsyncThunk(
  'customServiceRequest/review',
  async ({ requestId, adminId, approved }, { rejectWithValue }) => {
    try {
      const response = await customServiceRequestApi.reviewCustomServiceRequest({
        requestId,
        adminId,
        approved
      });
      message.success('审核成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '审核失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 开始服务
export const startService = createAsyncThunk(
  'customServiceRequest/startService',
  async ({ requestId, adminId }, { rejectWithValue }) => {
    try {
      const response = await customServiceRequestApi.startService({ requestId, adminId });
      message.success('服务开始');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '开始服务失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 完成服务
export const completeService = createAsyncThunk(
  'customServiceRequest/completeService',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await customServiceRequestApi.completeService(requestId);
      message.success('服务完成');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '完成服务失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 创建定制服务需求
export const createCustomServiceRequest = createAsyncThunk(
  'customServiceRequest/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await customServiceRequestApi.createCustomServiceRequest(data);
      message.success('创建成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '创建失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 定制服务请求状态常量
export const CUSTOM_SERVICE_REQUEST_STATUS = {
  SUBMITTED: 0,
  CONFIRMED: 1,
  PENDING_PAYMENT: 2,
  PAID: 3,
  IN_PROGRESS: 4,
  COMPLETED: 5,
  CANCELLED: 6
};

// 定制服务请求 Slice
const customServiceRequestSlice = createSlice({
  name: 'customServiceRequest',
  initialState: {
    list: [],
    currentRequest: null,
    loading: false,
    error: null,
    pagination: {
      total: 0,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchCustomServiceRequestList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomServiceRequestList.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload || {};
        state.list = data.list || [];
        state.pagination.total = data.total || 0;
        state.pagination.current = data.current || state.pagination.current;
        state.pagination.pageSize = data.pageSize || state.pagination.pageSize;
      })
      .addCase(fetchCustomServiceRequestList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取详情
      .addCase(fetchCustomServiceRequestDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomServiceRequestDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload;
      })
      .addCase(fetchCustomServiceRequestDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 更新
      .addCase(updateCustomServiceRequest.fulfilled, (state, action) => {
        const updatedRequest = action.payload;
        const index = state.list.findIndex(item => item.id === updatedRequest.id);
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...updatedRequest };
        }
        if (state.currentRequest && state.currentRequest.id === updatedRequest.id) {
          state.currentRequest = { ...state.currentRequest, ...updatedRequest };
        }
      })
      
      // 删除
      .addCase(deleteCustomServiceRequest.fulfilled, (state, action) => {
        state.list = state.list.filter(item => item.id !== action.payload);
        if (state.currentRequest && state.currentRequest.id === action.payload) {
          state.currentRequest = null;
        }
      })
      
      // 审核
      .addCase(reviewCustomServiceRequest.fulfilled, (state, action) => {
        const updatedRequest = action.payload;
        const index = state.list.findIndex(item => item.id === updatedRequest.id);
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...updatedRequest };
        }
        if (state.currentRequest && state.currentRequest.id === updatedRequest.id) {
          state.currentRequest = { ...state.currentRequest, ...updatedRequest };
        }
      })
      
      // 开始服务
      .addCase(startService.fulfilled, (state, action) => {
        const updatedRequest = action.payload;
        const index = state.list.findIndex(item => item.id === updatedRequest.id);
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...updatedRequest };
        }
        if (state.currentRequest && state.currentRequest.id === updatedRequest.id) {
          state.currentRequest = { ...state.currentRequest, ...updatedRequest };
        }
      })
      
      // 完成服务
      .addCase(completeService.fulfilled, (state, action) => {
        const updatedRequest = action.payload;
        const index = state.list.findIndex(item => item.id === updatedRequest.id);
        if (index !== -1) {
          state.list[index] = { ...state.list[index], ...updatedRequest };
        }
        if (state.currentRequest && state.currentRequest.id === updatedRequest.id) {
          state.currentRequest = { ...state.currentRequest, ...updatedRequest };
        }
      })
      
      // 创建
      .addCase(createCustomServiceRequest.fulfilled, (state, action) => {
        const newRequest = action.payload;
        state.list.unshift(newRequest);
        state.pagination.total += 1;
      });
  }
});

export const { setPagination } = customServiceRequestSlice.actions;
export default customServiceRequestSlice.reducer; 